import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { attendanceAPI, classFeesAPI } from '../../services/api';

const currentMonth = () => new Date().toISOString().slice(0, 7);

const AttendanceReport = () => {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [month, setMonth] = useState(currentMonth());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadClasses = async () => {
      try {
        const response = await classFeesAPI.getSettings();
        const activeClasses = Array.isArray(response.data.classes)
          ? response.data.classes.filter((cls) => Number(cls.is_active) === 1)
          : [];
        setClasses(activeClasses);
        if (activeClasses.length > 0) {
          setSelectedClass(activeClasses[0].standard);
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load classes');
      }
    };

    loadClasses();
  }, []);

  const loadReport = async () => {
    if (!selectedClass || !month) return;

    try {
      setLoading(true);
      setError('');
      const response = await attendanceAPI.getStats({
        standard: selectedClass,
        month,
      });
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load attendance report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass, month]);

  const totals = rows.reduce(
    (acc, row) => {
      acc.present += Number(row.present_days || 0);
      acc.absent += Number(row.absent_days || 0);
      acc.marked += Number(row.total_marked || 0);
      return acc;
    },
    { present: 0, absent: 0, marked: 0 }
  );
  const percentage = totals.marked > 0 ? ((totals.present / totals.marked) * 100).toFixed(2) : '0.00';

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography variant="h5" fontWeight="bold">
          Attendance Reports
        </Typography>
        <Button variant="outlined" startIcon={<Refresh />} onClick={loadReport} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <TextField
            select
            label="Class"
            value={selectedClass}
            onChange={(event) => setSelectedClass(event.target.value)}
            fullWidth
          >
            {classes.map((cls) => (
              <MenuItem key={cls.id} value={cls.standard}>
                {cls.display_name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Month"
            type="month"
            value={month}
            onChange={(event) => setMonth(event.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
              <Box>
                <Typography variant="caption" color="text.secondary">Present</Typography>
                <Typography variant="h6">{totals.present}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Absent</Typography>
                <Typography variant="h6">{totals.absent}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Attendance</Typography>
                <Typography variant="h6">{percentage}%</Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Enrollment No</TableCell>
                  <TableCell>Student</TableCell>
                  <TableCell align="right">Present</TableCell>
                  <TableCell align="right">Absent</TableCell>
                  <TableCell align="right">Marked</TableCell>
                  <TableCell align="right">Percentage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length > 0 ? rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.enrollment_no}</TableCell>
                    <TableCell>{row.student_name}</TableCell>
                    <TableCell align="right">{row.present_days}</TableCell>
                    <TableCell align="right">{row.absent_days}</TableCell>
                    <TableCell align="right">{row.total_marked}</TableCell>
                    <TableCell align="right">{row.attendance_percentage || 0}%</TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">No attendance records found.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default AttendanceReport;
